"use client";

import {
  IconAlertTriangleFilled,
  IconCircleCheckFilled,
  IconExclamationCircleFilled,
  IconInfoCircle,
  IconLoader2,
} from "@tabler/icons-react";
import { useStore } from "@tanstack/react-store";
import { useEffect, useState } from "react";
import { removeToast, toastsStore, type ToastItem } from "../stores/toasts";

const Toast = (props: ToastItem) => {
  const [visible, setVisible] = useState(true);
  const { onIgnore, duration } = props;

  useEffect(() => {
    if (!duration) return;
    if (!visible) return;

    const timer = setTimeout(() => {
      if (!visible) return;
      setVisible(false);
      onIgnore?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, visible, onIgnore]);

  useEffect(() => {
    if (!visible) {
      setTimeout(() => {
        removeToast(props.id);
      }, 300); // fade out animation duration.
    }
  }, [visible, props.id]);

  const type =
    props.type === "success"
      ? "success"
      : props.type === "error"
      ? "error"
      : props.type === "warning"
      ? "warning"
      : props.type === "pending"
      ? "pending"
      : "info";

  const bgColor =
    type === "success"
      ? "bg-green-300/25 dark:bg-green-700/40"
      : type === "error"
      ? "bg-red-300/25 dark:bg-red-700/40"
      : type === "warning"
      ? "bg-yellow-300/25 dark:bg-yellow-700/40"
      : type === "pending"
      ? "bg-blue-300/25 dark:bg-blue-700/40"
      : "bg-neutral-300/25 dark:bg-neutral-700/40";

  const textColor =
    type === "success"
      ? "text-green-700/80 dark:text-green-300/90"
      : type === "error"
      ? "text-red-700/80 dark:text-red-300/90"
      : type === "warning"
      ? "text-yellow-700/80 dark:text-yellow-300/90"
      : type === "pending"
      ? "text-blue-700/80 dark:text-blue-300/90"
      : "text-neutral-700/80 dark:text-neutral-300/90";

  const icon =
    props.icon ??
    (type === "success" ? (
      <IconCircleCheckFilled className="size-5 animate-zoom animation-delay-300" />
    ) : type === "error" ? (
      <IconExclamationCircleFilled className="size-5 animate-alert animation-delay-300" />
    ) : type === "warning" ? (
      <IconAlertTriangleFilled className="size-5 animate-wiggle repeat-1 animation-delay-300" />
    ) : type === "pending" ? (
      <IconLoader2 className="size-5 animate-spin animation-delay-300" />
    ) : (
      <IconInfoCircle className="size-5" />
    ));

  return (
    <div
      className={`
    mb-2 p-2 relative left-1/2 -translate-x-1/2 px-4 w-fit
    starting:translate-y-full starting:opacity-0 opacity-100 
    transition-all duration-300
    flex items-start gap-2
    cursor-pointer
    border-t border-white/20 dark:border-white/10 ${bgColor} ${textColor} font-medium text-sm
    backdrop-blur-sm rounded-xl`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateX(0)" : "translateX(50px)",
      }}
      onClick={() => {
        setVisible(false);
        props.onClick?.();
      }}
    >
      <span className="">{icon}</span>
      <span className="">{props.message}</span>
    </div>
  );
};

export const ToastStack = () => {
  const toasts = useStore(toastsStore);

  const removeToast = (index: number) => {
    setTimeout(() => {
      toastsStore.setState((old) => {
        const newToasts = [...old];
        newToasts.splice(index, 1);
        return newToasts;
      });
    }, 300); // fade out animation duration.
  };

  return (
    <div className={`fixed z-[1000000] top-30 w-full px-8`}>
      {toasts.map((toast, index) => (
        <Toast
          key={toast.id}
          {...toast}
          onClick={() => toast.onClick?.() || removeToast(index)}
          onIgnore={() => toast.onIgnore?.()}
        />
      ))}
    </div>
  );
};
